import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Heart, ChevronDown, ChevronUp, Lock } from 'lucide-react';
import { useAppStore } from '../store/appStore';
import { useContentAccess } from '../hooks/useContentAccess';
import {
  AFFIRMATIONS,
  AFFIRMATION_CATEGORIES,
  FREE_SAMPLE_COUNT,
  type AffirmationCategory,
} from '../data/affirmations';
import PremiumGate from '../components/PremiumGate';

const AGE_FILTERS = [
  { value: 'all', label: 'All ages' },
  { value: '4-6', label: 'Ages 4–6' },
  { value: '7-9', label: 'Ages 7–9' },
  { value: '10-12', label: 'Ages 10–12' },
] as const;

const cardVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, type: 'spring', stiffness: 220, damping: 22 },
  }),
};

export default function Affirmations() {
  const user = useAppStore((s) => s.user);
  const toggleSavedAffirmation = useAppStore((s) => s.toggleSavedAffirmation);
  const { isPremium } = useContentAccess();

  const [ageFilter, setAgeFilter] = useState<'all' | '4-6' | '7-9' | '10-12'>('all');
  const [categoryFilter, setCategoryFilter] = useState<AffirmationCategory | 'all'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showGate, setShowGate] = useState(false);

  const savedIds = user?.savedAffirmationIds ?? [];

  const filtered = AFFIRMATIONS.filter((a) => {
    const ageMatch =
      ageFilter === 'all' || a.ageGroups.includes(ageFilter as '4-6' | '7-9' | '10-12');
    const catMatch = categoryFilter === 'all' || a.category === categoryFilter;
    return ageMatch && catMatch;
  });

  const displayAffirmations = isPremium ? filtered : filtered.slice(0, FREE_SAMPLE_COUNT);
  const lockedCount = isPremium ? 0 : Math.max(0, filtered.length - FREE_SAMPLE_COUNT);

  const handleCardClick = (id: string, index: number) => {
    if (!isPremium && index >= FREE_SAMPLE_COUNT) {
      setShowGate(true);
      return;
    }
    setExpandedId(expandedId === id ? null : id);
  };

  const handleSave = (e: React.MouseEvent, affirmationId: string) => {
    e.stopPropagation();
    if (!isPremium) {
      setShowGate(true);
      return;
    }
    toggleSavedAffirmation(affirmationId);
  };

  return (
    <>
      <div className="px-4 pt-4 pb-24 space-y-5 max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="pt-10"
        >
          <div className="flex items-center gap-3 mb-1">
            <span className="text-3xl">✨</span>
            <h1 className="font-display text-2xl md:text-3xl font-extrabold text-gray-800">
              Affirmations
            </h1>
          </div>
          <p className="text-gray-400 text-sm leading-relaxed">
            Short, powerful phrases to read together. Tap any card to reveal the parent prompt.
          </p>
          {!isPremium && (
            <button
              onClick={() => setShowGate(true)}
              className="mt-3 inline-flex items-center gap-1.5 bg-warm-50 border border-warm-200 text-warm-700 text-xs font-bold px-3 py-2 rounded-full"
            >
              <Sparkles size={12} />
              Showing {Math.min(FREE_SAMPLE_COUNT, filtered.length)} of {AFFIRMATIONS.length} —
              Upgrade to unlock all
            </button>
          )}
        </motion.div>

        {/* Age filter tabs — premium only */}
        {isPremium && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="flex gap-2 overflow-x-auto pb-1 scrollbar-none"
          >
            {AGE_FILTERS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setAgeFilter(value)}
                className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  ageFilter === value
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300'
                }`}
              >
                {label}
              </button>
            ))}
          </motion.div>
        )}

        {/* Category filter — premium only */}
        {isPremium && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex gap-2 overflow-x-auto pb-1 scrollbar-none"
          >
            <button
              onClick={() => setCategoryFilter('all')}
              className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200 ${
                categoryFilter === 'all'
                  ? 'bg-gray-800 text-white shadow-sm'
                  : 'bg-white text-gray-500 border border-gray-100 hover:border-gray-300'
              }`}
            >
              All topics
            </button>
            {AFFIRMATION_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200 ${
                  categoryFilter === cat
                    ? 'bg-gray-800 text-white shadow-sm'
                    : 'bg-white text-gray-500 border border-gray-100 hover:border-gray-300'
                }`}
              >
                {cat}
              </button>
            ))}
          </motion.div>
        )}

        {/* Results count — premium only */}
        {isPremium && (
          <p className="text-xs text-gray-400">
            {filtered.length} affirmation{filtered.length !== 1 ? 's' : ''}
          </p>
        )}

        {/* Affirmation cards */}
        <div className="space-y-3">
          {displayAffirmations.map((aff, i) => {
            const isExpanded = expandedId === aff.id;
            const isSaved = savedIds.includes(aff.id);

            return (
              <motion.div
                key={aff.id}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                onClick={() => handleCardClick(aff.id, i)}
                className="bg-white rounded-2xl shadow-card p-4 cursor-pointer hover:shadow-card-hover transition-all duration-200"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <span className="inline-block bg-primary-50 text-primary-700 text-[10px] font-bold px-2 py-0.5 rounded-full mb-2">
                      {aff.category}
                    </span>
                    <p className="font-display font-bold text-gray-800 text-base leading-snug">
                      "{aff.text}"
                    </p>
                    <p className="text-gray-400 text-[11px] mt-1.5">
                      Ages {aff.ageGroups.join(', ')}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
                    {isPremium && (
                      <button
                        onClick={(e) => handleSave(e, aff.id)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          isSaved
                            ? 'text-rose-500 bg-rose-50'
                            : 'text-gray-300 hover:text-rose-400 hover:bg-rose-50'
                        }`}
                        title={isSaved ? 'Remove from favourites' : 'Save to favourites'}
                      >
                        <Heart size={15} fill={isSaved ? 'currentColor' : 'none'} />
                      </button>
                    )}
                    {isExpanded ? (
                      <ChevronUp size={16} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={16} className="text-gray-400" />
                    )}
                  </div>
                </div>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-1.5 mb-2">
                          <span className="text-base">💬</span>
                          <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                            Parent Prompt
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed">{aff.parentPrompt}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}

          {/* Locked upgrade prompt for non-premium */}
          {!isPremium && lockedCount > 0 && (
            <motion.button
              custom={FREE_SAMPLE_COUNT}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              onClick={() => setShowGate(true)}
              className="w-full bg-gradient-to-br from-warm-50 to-amber-50 border-2 border-dashed border-warm-200 rounded-2xl p-6 text-center hover:border-warm-300 transition-colors"
            >
              <div className="text-4xl mb-3">✨</div>
              <p className="font-display font-extrabold text-warm-800 text-lg mb-1">
                {lockedCount} more affirmation{lockedCount !== 1 ? 's' : ''} waiting
              </p>
              <p className="text-warm-600 text-sm mb-4">
                Upgrade to Premium to unlock all {AFFIRMATIONS.length} affirmations with parent prompts, age filters, and favourites.
              </p>
              <span className="inline-flex items-center gap-2 bg-warm-400 text-white text-sm font-bold px-5 py-2.5 rounded-xl">
                <Lock size={14} />
                Upgrade to Premium →
              </span>
            </motion.button>
          )}

          {/* Saved affirmations section — premium only */}
          {isPremium && savedIds.length > 0 && ageFilter === 'all' && categoryFilter === 'all' && (
            <div className="mt-2 pt-4 border-t border-gray-100">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <Heart size={12} className="text-rose-400" fill="currentColor" />
                Your favourites ({savedIds.length})
              </p>
              <div className="space-y-2">
                {AFFIRMATIONS.filter((a) => savedIds.includes(a.id)).map((aff) => (
                  <div
                    key={`saved-${aff.id}`}
                    className="bg-rose-50 rounded-xl px-4 py-3 flex items-center justify-between gap-3"
                  >
                    <p className="font-display font-semibold text-gray-800 text-sm leading-snug flex-1 min-w-0">
                      "{aff.text}"
                    </p>
                    <button
                      onClick={() => toggleSavedAffirmation(aff.id)}
                      className="shrink-0 p-1.5 rounded-lg text-rose-400 hover:text-rose-600 hover:bg-rose-100 transition-colors"
                    >
                      <Heart size={14} fill="currentColor" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <PremiumGate
        visible={showGate}
        feature="affirmations"
        onDismiss={() => setShowGate(false)}
      />
    </>
  );
}
